import { useCallback, useMemo, useState } from 'react';

import { useIsFocused } from '@react-navigation/native';

import { useLeagueUpcomingEventsQuery } from '@/api/events/query.generated';
import { useLiveEventsPropsUpdate } from '@/feature/betslip-pickem/hooks/use-live-props-update';
import { useUpdatePlayerStoreWithNewData } from '@/feature/betslip-pickem/hooks/use-player-props-store';
import { useGQLRequestPolicy } from '@/feature/guest-user/hooks/use-gql-request-policy';
import { useLeagueContentRenderer } from '@/feature/lobby/hooks/use-league-content-render';
import { TileType } from '@/feature/lobby/hooks/use-lobby-section-selection';
import { useResumeEffectWithDelay } from '@/hooks/use-resume';
import { League } from '@/types/api.generated';
import { sortEvents } from '@/utils/sortEvents';
import { sortLobbyPlayers } from '@/utils/sortPlayers';

import { FILTER_MODES_TABS } from '../utils/filters';
import { generateLobbyPlayerCards } from '../utils/lobbyPlayers';
import { useFantasyLobbyQueryWithVariables } from './use-fantasy-lobby-query-with-variables';
import { useLobbyContentRender } from './use-lobby-content-render';
import { useSortedLobbyPlayers } from './use-lobby-data';

/**
 * Hook to compute the data to be displayed on the lobby screen based on the selection.
 * This can be the home / lobby data, or league specific data.
 *
 * This hook will fetch the required data based on the selection and return the data to be displayed.
 * It will also return the render functions to render the data.
 */
export const useFantasyLobbyContent = ({ selectedTile }: { selectedTile: TileType }) => {
    const isLobby = selectedTile === 'lobby';
    const leagueId = (selectedTile !== 'lobby' ? selectedTile : undefined) as League;
    const [filterType, setFilterMode] = useState(FILTER_MODES_TABS[0].filterType);

    const requestPolicy = useGQLRequestPolicy('cache-and-network');

    const isFocused = useIsFocused();

    // Fetch favorites, top 10, lobby players, shared lineups
    const [{ data: lobbyData }, fetchLobbyContent] = useFantasyLobbyQueryWithVariables({
        requestPolicy,
        pause: !isLobby,
    });
    useLiveEventsPropsUpdate({
        events: lobbyData?.getUpcomingLobbyEventsV2,
        options: {
            //only subscribe to the lobby event updates if we are in the lobby and the screen is focused
            subscribeToGameClockUpdates: isLobby && isFocused,
            subscribeToMarketUpdates: isLobby && isFocused,
            debugTag: 'useFantasyLobbyContent-lobby',
        },
    });
    useUpdatePlayerStoreWithNewData(lobbyData?.getUpcomingLobbyEventsV2);

    const sortedLobbyPlayers = useMemo(() => {
        //this sort can be removed when BE will send the sorted trending players
        return generateLobbyPlayerCards(lobbyData?.getUpcomingLobbyEventsV2 ?? []).sort(sortLobbyPlayers);
    }, [lobbyData?.getUpcomingLobbyEventsV2]);

    // Fetch specific league events and players
    const [{ data: leagueEventsWithPlayers, fetching: fetchingLeagueEventsData }, fetchLeagueContent] =
        useLeagueUpcomingEventsQuery({
            variables: { league: leagueId },
            requestPolicy,
            pause: !leagueId,
        });

    useLiveEventsPropsUpdate({
        events: leagueEventsWithPlayers?.getUpcomingEventsV2,
        options: {
            subscribeToGameClockUpdates: !isLobby && isFocused,
            subscribeToMarketUpdates: !isLobby && isFocused,
            debugTag: `useFantasyLobbyContent-${leagueId}`,
        },
    });
    useUpdatePlayerStoreWithNewData(leagueEventsWithPlayers?.getUpcomingEventsV2);

    const sortedEvents = useMemo(
        () => (leagueEventsWithPlayers?.getUpcomingEventsV2 || []).sort((event1, event2) => sortEvents(event1, event2)),
        [leagueEventsWithPlayers?.getUpcomingEventsV2]
    );

    const sortedPlayers = useSortedLobbyPlayers({
        sortedEvents,
        filterType,
        league: leagueId,
    });

    const hasStaleData =
        leagueEventsWithPlayers?.getUpcomingEventsV2.length !== undefined &&
        leagueEventsWithPlayers.getUpcomingEventsV2[0]?.league !== leagueId &&
        leagueEventsWithPlayers.getUpcomingEventsV2.length !== 0;

    const events = useMemo(() => {
        return hasStaleData ? [] : sortedEvents;
    }, [hasStaleData, sortedEvents]);

    const refreshData = useCallback(() => {
        if (isLobby) {
            fetchLobbyContent();
        } else {
            fetchLeagueContent();
        }
    }, [fetchLeagueContent, fetchLobbyContent, isLobby]);

    useResumeEffectWithDelay(refreshData);

    const { renderPlayerItem, renderEventCards, renderFilters, renderLeagueFooter } = useLeagueContentRenderer({
        league: leagueId,
        selectedFilter: filterType,
        selectFilter: setFilterMode,
        sortedEvents: events,
        sortedPlayers,
        eventsLoading: fetchingLeagueEventsData || hasStaleData,
    });

    const { renderLobbyPlayerItem, renderLobbyComponent } = useLobbyContentRender({ players: sortedLobbyPlayers });

    return {
        sortedPlayers,
        sortedLobbyPlayers,
        renderLobbyPlayerItem,
        renderLobbyComponent,
        renderPlayerItem,
        renderEventCards,
        renderFilters,
        renderLeagueFooter,
    };
};
